import { createObservableRef } from '@jotta/hooks'
import { getFilenameFromPath } from '@jotta/utils/pathUtils'
import type { Debugger } from 'debug'
import Debug from 'debug'
import { action, makeAutoObservable, toJS, values } from 'mobx'
import type { ChangeEventHandler } from 'react'
import type { FolderStore } from './FolderStore'
import { ObservableCaseInsensitiveSet } from '@jotta/collection/ObservableCaseInsensitiveSet'

const debug = Debug(`jotta:files:selection`)

export class FolderSelectionStore {
  _selection = new ObservableCaseInsensitiveSet()
  private _log: Debugger
  private _folder: FolderStore
  private _lastSelection = {
    selected: false,
    id: '',
  }
  constructor(folder: FolderStore) {
    this._folder = folder
    this._log = folder.log.extend('selection')
    this._log('Create')
    makeAutoObservable<typeof this, '_log'>(
      this,
      {
        _log: false,
      },
      {
        autoBind: true,
      },
    )
  }
  headerInputRef = createObservableRef<HTMLInputElement>()
  onSelectCheckboxChange = action<ChangeEventHandler<HTMLInputElement>>(
    'onSelectChange',
    e => {
      const id = e.target.value
      const selected = e.target.checked
      const nativeEvent = e.nativeEvent
      const shiftPressed =
        nativeEvent instanceof MouseEvent ? nativeEvent.shiftKey : false
      const lastSelection = this._lastSelection
      const lastId = lastSelection.id
      const lastSelected = lastSelection.selected
      this._log('onSelectChange', {
        id,
        selected,
        shiftPressed,
        lastId,
      })
      this._lastSelection = {
        id,
        selected,
      }
      // this._log(e.target.value, [...this.ids])
      if (shiftPressed && lastId) {
        this.selectRangeById(id, lastId, lastSelected)
        return
      }
      if (selected) {
        this.select(id)
        return
      }

      this.unselect(id)
    },
  )

  select(id: string) {
    // this._log('Select', id)
    this._selection.add(id)
  }
  unselect(id: string) {
    // this._log('Unselect', id)
    this._selection.delete(id)
  }
  setIndeterminate(isIndeterminate: boolean) {
    const el = this.headerInputRef.current
    if (el) {
      el.indeterminate = isIndeterminate
    }
  }
  toggle(id: string) {
    if (this._selection.has(id)) {
      this.unselect(id)
    } else {
      this.select(id)
    }
  }
  selectAll() {
    this._selection.replace(toJS(this._folder.ids))
  }
  selectRangeById(id1: string, id2: string, selected: boolean) {
    const idArray = this._folder.children.map(value => value.path)
    const [i1, i2] = [idArray.indexOf(id1), idArray.indexOf(id2)].sort()
    if (i1 >= 0 && i2 >= 0) {
      const ids = idArray.slice(i1, i2 + 1)
      // this._log('selectRangeById', id1, id2, i1, i2, selected, ids)
      if (selected) {
        for (const id of ids) {
          this._selection.add(id)
        }
      } else {
        for (const id of ids) {
          this._selection.delete(id)
        }
      }
    }
  }
  selectNone() {
    this._selection.clear()
  }
  toggleSelectAll() {
    if (!this.allSelected) {
      this._log('Toggle select: all')
      this.selectAll()
    } else {
      this._log('Toggle select: none')
      this.selectNone()
    }
  }
  clear() {
    this._selection.clear()
  }
  get ids() {
    return toJS(this._folder.ids)
  }
  get total() {
    return this._selection.size
  }

  get hasSelection() {
    return Boolean(this._selection.size)
  }
  get allSelected() {
    return this._selection.size === this._folder.ids.size
  }
}
